import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";

/** เปลี่ยนรหัสผ่าน (Ben, 2026-10-02 — replaces the รหัสสำรอง switch). `forced` = the login is still on its first /
 *  reset password `<username>1234`: the dialog cannot be closed and asks no current password, and the server
 *  answers every other API with 403 must_change until it is done. The request never names a user. */
export function PasswordDialog({ open, onOpenChange, forced = false }: { open: boolean; onOpenChange: (o: boolean) => void; forced?: boolean }) {
  const { user, refresh, logout } = useAuth();
  const { push } = useToast();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  async function save() {
    if (!next || (!forced && !cur)) return push({ title: "กรอกรหัสให้ครบ", variant: "error" });
    setBusy(true);
    try {
      await api.changeMyPassword({ current: forced ? undefined : cur, next, confirm: conf });
      setCur(""); setNext(""); setConf("");
      push({ title: "ตั้งรหัสผ่านใหม่แล้ว" });
      await refresh();
      if (!forced) onOpenChange(false);
    } catch (e: any) {
      push({ title: "ไม่สำเร็จ", description: e.message, variant: "error" });
    } finally {
      setBusy(false);
    }
  }

  const row = (k: string, v: React.ReactNode) => (
    <tr className="border-b border-border/60 last:border-0">
      <th className="w-32 bg-muted/30 px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{k}</th>
      <td className="px-3 py-2">{v}</td>
    </tr>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!forced) onOpenChange(o); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{forced ? "เข้าครั้งแรก — ตั้งรหัสผ่านของคุณเอง" : "เปลี่ยนรหัสผ่าน"}</DialogTitle>
        </DialogHeader>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <tbody>
              {!forced && row("รหัสปัจจุบัน", <Input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />)}
              {row("รหัสใหม่", <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="6 ตัวขึ้นไป" />)}
              {row("ยืนยันรหัสใหม่", <Input type="password" autoComplete="new-password" value={conf} onChange={(e) => setConf(e.target.value)} />)}
              <tr>
                <td colSpan={2} className="px-3 py-2 text-xs text-muted-foreground">
                  {forced ? `ห้ามใช้ ${user.username.toLowerCase()}1234 · ` : ""}{user.sq === false ? "ลืมรหัส → ขอแอดมินกดรีเซ็ต" : "รหัสเดียวกันทุกเว็บ BBT · ลืมรหัส → แจ้งผู้จัดการ"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy || !next}>บันทึก</Button>
          {forced && <Button variant="outline" onClick={() => logout()}>ออกจากระบบ</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
